/**
 * Recent-repos list shown on the picker. Session-scoped so it dies with
 * the tab (same discipline as the PAT). Wiped on signOut via wipeSession.
 *
 * A module-scope listener set publishes mutations so `RepoPicker` (and any
 * future subscriber) can read it via useSyncExternalStore and re-render
 * when the list changes from anywhere — e.g. a CSV import performed from
 * the header's cache menu, not from the picker itself.
 */

const RECENT_KEY = "gha_recent_repos";
const MAX_RECENT = 8;

let cached: string[] | undefined;
const listeners = new Set<() => void>();

function readFromStorage(): string[] {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((s): s is string => typeof s === "string")
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeToStorage(slugs: string[]): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(RECENT_KEY, JSON.stringify(slugs.slice(0, MAX_RECENT)));
}

function notify(): void {
  for (const cb of listeners) cb();
}

/** Synchronous snapshot — stable reference until a mutation fires. */
export function loadRecentRepos(): string[] {
  if (cached === undefined) cached = readFromStorage();
  return cached;
}

/** Subscribe to mutations. Returns an unsubscribe function. */
export function subscribeRecentReposChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Move `slug` to the top of the list (or prepend if missing). */
export function saveRecentRepo(slug: string): void {
  if (!slug) return;
  const current = loadRecentRepos().filter((s) => s !== slug);
  const next = [slug, ...current].slice(0, MAX_RECENT);
  cached = next;
  writeToStorage(next);
  notify();
}

/**
 * Merge a batch of `owner/name` slugs into the recent list. Incoming slugs
 * go on top in the order provided; existing entries not in the incoming
 * batch are preserved below. Returns the number of new slugs added.
 */
export function saveRecentRepos(incoming: string[]): number {
  // Deduplicate while preserving order.
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const slug of incoming) {
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    ordered.push(slug);
  }
  if (ordered.length === 0) return 0;

  const existing = loadRecentRepos();
  const existingSet = new Set(existing);
  const newCount = ordered.filter((s) => !existingSet.has(s)).length;

  const merged: string[] = [...ordered];
  for (const slug of existing) {
    if (!seen.has(slug)) merged.push(slug);
  }
  const next = merged.slice(0, MAX_RECENT);
  cached = next;
  writeToStorage(next);
  notify();
  return newCount;
}

/** Wipe the list. Called by wipeSession so sign-out / 401 also flushes it. */
export function clearRecentRepos(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(RECENT_KEY);
  cached = [];
  notify();
}
